import {
  LuTicket,
  LuPlus,
  LuLoader,
  LuTrash2,
  LuCopy,
  LuTags,
  LuFileSpreadsheet
} from "react-icons/lu";

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "@lib/firebase";
import { collection, getDocs, deleteDoc, doc } from "firebase/firestore";
import { useNotification } from "../../common/NotificationContext";
import { useAuth } from "../../context/AuthContext";

// Helper to format dates
const formatDate = (dateValue) => {
  if (!dateValue) return "-";
  let date;
  if (dateValue.seconds) {
    date = new Date(dateValue.seconds * 1000);
  } else if (dateValue instanceof Date) {
    date = dateValue;
  } else {
    date = new Date(dateValue);
  }
  if (isNaN(date.getTime())) return "-";
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const Vouchers = () => {
  const navigate = useNavigate();
  const { getIdToken } = useAuth();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const { showNotification } = useNotification();

  useEffect(() => {
    fetchVouchers();
  }, []);

  const fetchVouchers = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, "vouchers"));
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Filter only JOTR2026 or no eventId
      setVouchers(list.filter((v) => !v.eventId || v.eventId === "JOTR2026"));
    } catch (error) {
      console.error("Error fetching vouchers:", error);
      showNotification("error", "Failed to load vouchers");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (voucher) => {
    if (!confirm(`Delete voucher "${voucher.code}"?`)) return;

    try {
      await deleteDoc(doc(db, "vouchers", voucher.id));
      showNotification("success", "Voucher deleted");
      fetchVouchers();
    } catch (error) {
      console.error("Error deleting voucher:", error);
      showNotification("error", "Failed to delete voucher");
    }
  };

  // Format discount display
  const getDiscountDisplay = (v) => {
    if (v.discountType === "fixed") {
      return `Rp ${(v.discountValue || 0).toLocaleString('en-US')}`;
    }
    return `${v.discountValue || v.discountPercentage || 0}%`;
  };

  // Copy code to clipboard
  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    showNotification("success", `Code "${code}" copied to clipboard`);
  };

  const handleExportSheets = async () => {
    const spreadsheetId = "1abiqc_s2cODo9VVr7kc1Grxajaa6fj-ReV-yRlDg5rk";
    setExporting(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/export-vouchers-sheets`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await getIdToken()}`,
        },
        body: JSON.stringify({ spreadsheetId }),
      });

      const result = await response.json();
      if (response.ok) {
        showNotification("success", "Vouchers exported to Google Sheets!");
        window.open(result.url, "_blank");
      } else {
        showNotification("error", result.error || "Failed to export vouchers");
      }
    } catch (error) {
      console.error("Export error:", error);
      showNotification("error", "Error exporting to Google Sheets");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LuLoader className="animate-spin text-4xl text-slate-800" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Dashboard Style */}
      <div className="bg-white rounded-xl p-4 border border-slate-200 text-slate-800 relative overflow-hidden">
         <div className="absolute top-0 right-0 p-4 opacity-5 text-slate-200 pointer-events-none">
            <LuTicket size={120} />
         </div>
         <div className="relative z-10">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
               <div>
                  <p className="text-slate-500 font-medium mb-1 flex items-center gap-2 text-xs uppercase tracking-wider">
                     <LuTags size={14} /> Promotion
                  </p>
                   <h1 className="text-2xl md:text-3xl font-bold mb-2 break-words">Voucher Management</h1>
                   <p className="text-slate-500 text-sm max-w-xl">
                       Create, manage, and track voucher usage for registrations.
                   </p>
               </div>
               <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-3">
                   <button
                       onClick={handleExportSheets}
                       disabled={exporting}
                       className="w-full md:w-auto inline-flex justify-center items-center gap-2 bg-green-600 text-white px-4 py-3 md:py-2 rounded-xl hover:bg-green-700 transition-colors text-sm font-medium h-fit disabled:opacity-50"
                   >
                       {exporting ? <LuLoader className="animate-spin" /> : <LuFileSpreadsheet className="text-white" />}
                       Google Sheets
                   </button>
                   <button
                       onClick={() => navigate("/admin/vouchers/create")}
                       className="w-full md:w-auto bg-slate-800 hover:bg-slate-900 text-white px-4 py-3 md:py-2 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 h-fit"
                   >
                       <LuPlus /> Add Voucher
                   </button>
               </div>
            </div>
         </div>
      </div>

      {/* Voucher Count */}
      <div className="text-sm text-slate-500">
        Showing <span className="font-semibold text-slate-700">{vouchers.length}</span> of <span className="font-semibold text-slate-700">{vouchers.length}</span> vouchers
      </div>

      {/* Voucher Table */}
      <div className="bg-white rounded-xl overflow-hidden border border-slate-200">
        {vouchers.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No vouchers found. Create one to get started.</div>
        ) : (
          
          <>
            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
              {vouchers.map((v) => (
                <div key={v.id} className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-2">
                      <LuTicket className="text-slate-400" />
                      <span className="font-bold text-slate-800 text-lg">{v.code}</span>
                      <button
                        onClick={() => handleCopyCode(v.code)}
                        className="text-slate-400 hover:text-slate-600 transition-colors"
                        title="Copy code"
                      >
                         <LuCopy size={14} />
                      </button>
                    </div>
                    {v.isActive ? (
                        <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-bold uppercase">Active</span>
                      ) : (
                        <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-bold uppercase">Inactive</span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Discount</p>
                      <p className="font-medium text-slate-800">{getDiscountDisplay(v)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Usage</p>
                      <p className="font-medium text-slate-800">{v.usageCount || 0} / {v.maxUsage || "∞"}</p>
                    </div>
                    <div>
                         <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Created</p>
                         <p className="text-sm text-slate-600">{formatDate(v.createdAt)}</p>
                    </div>
                    <div>
                         <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Expires</p>
                         <p className="text-sm text-slate-600">{formatDate(v.expiredAt)}</p>
                    </div>
                  </div>

                  <div className="flex gap-2 border-t border-slate-100 pt-3">
                     <button
                        onClick={() => navigate(`/admin/vouchers/${v.id}`)}
                        className="flex-1 bg-slate-50 hover:bg-slate-100 text-slate-600 py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => handleDelete(v)}
                        className="bg-red-50 hover:bg-red-100 text-red-600 px-4 py-2 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <LuTrash2 size={16} />
                      </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Code</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Discount</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Usage</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Created At</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Expires</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Status</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {vouchers.map((v) => (
                    <tr key={v.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleCopyCode(v.code)}
                            className="text-slate-400 hover:text-slate-600 transition-colors"
                            title="Copy code"
                          >
                            <LuCopy size={14} />
                          </button>
                          <span className="font-semibold text-slate-800">{v.code}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-slate-700 font-medium">
                          {getDiscountDisplay(v)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {v.usageCount || 0} / {v.maxUsage || "∞"}
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-sm">
                        {formatDate(v.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-sm">
                        {formatDate(v.expiredAt)}
                      </td>
                      <td className="px-4 py-3">
                        {v.isActive ? (
                          <span className="text-green-600 font-medium text-sm">Active</span>
                        ) : (
                          <span className="text-red-500 font-medium text-sm">Inactive</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={() => navigate(`/admin/vouchers/${v.id}`)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleDelete(v)}
                            className="text-slate-400 hover:text-red-600 transition-colors"
                            title="Delete"
                          >
                            <LuTrash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
